import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Building, Mail, Phone, AlertCircle, FileText, Download, Image, X, ZoomIn, Archive, UserPlus } from 'lucide-react';
import axios from 'axios';

const SubmissionDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [detailState, setDetailState] = useState({
    loading: true,
    error: null,
    submission: null
  });
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    currentImage: null,
    currentIndex: 0
  });

  const [archiveState, setArchiveState] = useState({
    showArchiveConfirm: false,
    isArchiving: false,
    archiveReason: ''
  });

  const [assignmentState, setAssignmentState] = useState({
    users: [],
    loadingUsers: false,
    showAssignModal: false,
    isAssigning: false,
    selectedUserId: null
  });

  useEffect(() => {
    loadSubmissionDetail();
    loadAssignableUsers();
  }, [id]);

  const loadSubmissionDetail = async () => {
    try {
      // Check authentication first
      const authResponse = await axios.get('/api/auth/status');
      if (!authResponse.data.isAuthenticated) {
        navigate('/admin');
        return;
      }

      // Load submission detail
      const response = await axios.get(`/api/submissions/${id}`);
      setDetailState({
        loading: false,
        error: null,
        submission: response.data
      });
    } catch (error) {
      console.error('Load submission detail error:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      } else if (error.response?.status === 404) {
        setDetailState({
          loading: false,
          error: 'Submission not found',
          submission: null
        });
      } else {
        setDetailState({
          loading: false,
          error: 'Failed to load submission details',
          submission: null
        });
      }
    }
  };

  const formatValue = (value) => {
    return value || 'Not provided';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    
    if (mb >= 1) {
      return `${mb.toFixed(1)}MB`;
    } else {
      return `${kb.toFixed(1)}KB`;
    }
  };

  const openImageModal = (image, index) => {
    setImageModal({
      isOpen: true,
      currentImage: image,
      currentIndex: index
    });
  };

  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      currentImage: null,
      currentIndex: 0
    });
  };

  const navigateImage = (direction) => {
    const images = submission.submission_images || [];
    const newIndex = direction === 'next' 
      ? (imageModal.currentIndex + 1) % images.length
      : (imageModal.currentIndex - 1 + images.length) % images.length;
    
    setImageModal({
      ...imageModal,
      currentImage: images[newIndex],
      currentIndex: newIndex
    });
  };

  // Assignment handlers
  const loadAssignableUsers = async () => {
    setAssignmentState(prev => ({ ...prev, loadingUsers: true }));
    
    try {
      const response = await axios.get('/api/users/assignable');
      setAssignmentState(prev => ({
        ...prev,
        users: response.data.users,
        loadingUsers: false
      }));
    } catch (error) {
      console.error('Load assignable users error:', error);
      setAssignmentState(prev => ({ ...prev, loadingUsers: false }));
    }
  };

  const handleAssignClick = () => {
    setAssignmentState(prev => ({ ...prev, showAssignModal: true }));
  };

  const handleAssignCancel = () => {
    setAssignmentState(prev => ({ 
      ...prev, 
      showAssignModal: false, 
      selectedUserId: null 
    }));
  };

  const handleAssign = async () => {
    setAssignmentState(prev => ({ ...prev, isAssigning: true }));

    try {
      await axios.put(`/api/submissions/${id}/assign`, {
        userId: assignmentState.selectedUserId
      });

      // Reload submission to get updated assignment data
      await loadSubmissionDetail();

      // Close modal
      setAssignmentState({
        users: assignmentState.users,
        loadingUsers: false,
        showAssignModal: false,
        isAssigning: false,
        selectedUserId: null
      });

    } catch (error) {
      console.error('Assignment error:', error);
      setDetailState(prev => ({
        ...prev,
        error: error.response?.data?.error || 'Failed to assign submission'
      }));
      setAssignmentState(prev => ({ ...prev, isAssigning: false }));
    }
  };

  const formatAssignedTo = (assignedTo) => {
    if (!assignedTo) return 'Unassigned';
    if (assignedTo.firstName && assignedTo.lastName) {
      return `${assignedTo.firstName} ${assignedTo.lastName}`;
    }
    return assignedTo.username;
  };

  // Archive handlers
  const handleArchiveConfirm = () => {
    setArchiveState(prev => ({ ...prev, showArchiveConfirm: true }));
  };

  const handleArchiveCancel = () => {
    setArchiveState({
      showArchiveConfirm: false,
      isArchiving: false,
      archiveReason: ''
    });
  };

  const handleArchive = async () => {
    setArchiveState(prev => ({ ...prev, isArchiving: true }));

    try {
      await axios.post(`/api/submissions/${id}/archive`, {
        reason: archiveState.archiveReason
      });

      // Redirect back to dashboard after successful archive
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Archive error:', error);
      setDetailState(prev => ({
        ...prev,
        error: error.response?.data?.error || 'Failed to archive submission'
      }));
      setArchiveState(prev => ({ 
        ...prev, 
        isArchiving: false, 
        showArchiveConfirm: false,
        archiveReason: ''
      }));
    }
  };

  if (detailState.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading submission details...</p>
        </div>
      </div>
    );
  }

  if (detailState.error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Submission</h3>
          <p className="text-gray-600 mb-4">{detailState.error}</p>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Back to Dashboard
            </button>
            <button
              onClick={loadSubmissionDetail}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const submission = detailState.submission;

  const DetailSection = ({ title, children, className = "" }) => (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b border-gray-200 pb-2">
        {title}
      </h3>
      {children}
    </div>
  );

  const DetailField = ({ label, value, className = "" }) => (
    <div className={`${className}`}>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{formatValue(value)}</dd>
    </div>
  );

  const DetailGrid = ({ children }) => (
    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {children}
    </dl>
  );

  const SubSection = ({ title, children, borderColor = "border-gray-400" }) => (
    <div className={`border-l-4 ${borderColor} pl-4 mb-4`}>
      <h4 className="font-medium text-gray-700 mb-3">{title}</h4>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-md"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Submission #{submission.id}
                </h1>
                <p className="text-sm text-gray-500">
                  Submitted on {formatDate(submission.created_at)}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleArchiveConfirm}
                disabled={archiveState.isArchiving}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Archive className="w-4 h-4 mr-2" />
                {archiveState.isArchiving ? 'Archiving...' : 'Archive Submission'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Building className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Company</p>
                <p className="text-lg font-semibold text-gray-900 truncate">
                  {formatValue(submission.company)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <User className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Contact</p>
                <p className="text-lg font-semibold text-gray-900 truncate">
                  {formatValue(submission.contact_person)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Mail className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Email</p>
                <p className="text-lg font-semibold text-gray-900 truncate">
                  {formatValue(submission.email)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-orange-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Submitted</p>
                <p className="text-lg font-semibold text-gray-900">
                  {new Date(submission.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Assignment Section */}
        <DetailSection title="Assignment Information">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {submission.assigned_to ? (
                <div className="flex items-center">
                  <User className="h-5 w-5 text-blue-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Assigned to: {formatAssignedTo(submission.assigned_to)}
                    </p>
                    {submission.assigned_at && (
                      <p className="text-xs text-gray-500">
                        Assigned on {formatDate(submission.assigned_at)}
                        {submission.assigned_by && ` by ${formatAssignedTo(submission.assigned_by)}`}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center">
                  <div className="h-5 w-5 rounded-full bg-gray-400 mr-2"></div>
                  <p className="text-sm text-gray-500">Not assigned</p>
                </div>
              )}
            </div>
            <button
              onClick={handleAssignClick}
              disabled={assignmentState.isAssigning}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {submission.assigned_to ? 'Reassign' : 'Assign'}
            </button>
          </div>
        </DetailSection>

        <div className="space-y-6">
          {/* Customer Information */}
          <DetailSection title="Customer Information">
            <DetailGrid>
              <DetailField label="Company" value={submission.company} />
              <DetailField label="Contact Person" value={submission.contact_person} />
              <DetailField label="Phone/Fax" value={submission.phone_fax} />
              <DetailField label="Email" value={submission.email} />
              <DetailField 
                label="Address" 
                value={submission.address} 
                className="sm:col-span-2 lg:col-span-3" 
              />
            </DetailGrid>
          </DetailSection>

          {/* Machine Information */}
          <DetailSection title="Machine Information">
            <DetailGrid>
              <DetailField label="Machine Type" value={submission.machine_type} />
              <DetailField label="Machine Name" value={submission.machine_name} />
            </DetailGrid>
          </DetailSection>

          {/* Oil Information */}
          <DetailSection title="Oil Information">
            <DetailGrid>
              <DetailField label="Oil Type" value={submission.oil_type} />
              <DetailField label="Oil Name" value={submission.oil_name} />
              <DetailField label="Viscosity" value={submission.viscosity} />
              <DetailField label="Flash Point" value={submission.flash_point} />
            </DetailGrid>
            {submission.oil_comments && (
              <div className="mt-4">
                <dt className="text-sm font-medium text-gray-500">Oil Comments</dt>
                <dd className="mt-1 text-sm text-gray-900">{submission.oil_comments}</dd>
              </div>
            )}
          </DetailSection>

          {/* System Information */}
          <DetailSection title="System Information">
            <DetailGrid>
              <DetailField label="System Volume" value={submission.system_volume} />
              <DetailField label="Main Pump Capacity/h" value={submission.main_pump_capacity} />
              <DetailField label="Tank Volume" value={submission.tank_volume} />
            </DetailGrid>
            {submission.tank_comments && (
              <div className="mt-4">
                <dt className="text-sm font-medium text-gray-500">Tank Comments</dt>
                <dd className="mt-1 text-sm text-gray-900">{submission.tank_comments}</dd>
              </div>
            )}
          </DetailSection>

          {/* Temperature */}
          <DetailSection title="Temperature">
            <DetailGrid>
              <DetailField label="Operational (°C)" value={submission.temp_normal} />
              <DetailField label="Maximum (°C)" value={submission.temp_max} />
              <DetailField label="Start Up (°C)" value={submission.temp_min} />
            </DetailGrid>
          </DetailSection>

          {/* Power Supply */}
          <DetailSection title="Power Supply">
            <DetailGrid>
              <DetailField label="Electrical Supply (V)" value={submission.electrical_voltage} />
              <DetailField label="Frequency (Hz)" value={submission.electrical_hz} />
            </DetailGrid>
            {submission.power_comments && (
              <div className="mt-4">
                <dt className="text-sm font-medium text-gray-500">Power Comments</dt>
                <dd className="mt-1 text-sm text-gray-900">{submission.power_comments}</dd>
              </div>
            )}
          </DetailSection>

          {/* Heating */}
          <DetailSection title="Heating">
            <div className="space-y-4">
              <SubSection title="Electrical" borderColor="border-blue-400">
                <DetailGrid>
                  <DetailField label="Voltage (V)" value={submission.preheat_electrical_v} />
                  <DetailField label="Frequency (Hz)" value={submission.preheat_electrical_hz} />
                </DetailGrid>
              </SubSection>
              
              
              {submission.preheat_comments && (
                <div className="mt-4">
                  <dt className="text-sm font-medium text-gray-500">Preheat Comments</dt>
                  <dd className="mt-1 text-sm text-gray-900">{submission.preheat_comments}</dd>
                </div>
              )}
            </div>
          </DetailSection>


          {/* Present Condition */}
          <DetailSection title="Present Condition">
            <div className="space-y-4">
              <SubSection title="Solid Contaminants" borderColor="border-yellow-400">
                <DetailGrid>
                  <DetailField label="ISO/NAS" value={submission.solid_contaminants} />
                  <DetailField label="Other" value={submission.solid_contaminants_other} />
                  <DetailField label="Ingress per Hour" value={submission.solid_ingress_per_hour} />
                </DetailGrid>
              </SubSection>
              
              <SubSection title="Water Contamination" borderColor="border-purple-400">
                <DetailGrid>
                  <DetailField label="Percentage (%)" value={submission.water_contamination_percent} />
                  <DetailField label="PPM" value={submission.water_contamination_ppm} />
                  <DetailField label="Ingress per Hour" value={submission.water_ingress_per_hour} />
                </DetailGrid>
              </SubSection>

              {/* Oil Analysis PDF */}
              {submission.oil_analysis_pdf && (
                <SubSection title="Oil Analysis Report" borderColor="border-blue-400">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="h-6 w-6 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-blue-900">
                            {submission.oil_analysis_original_name || 'Oil Analysis Report.pdf'}
                          </p>
                          <p className="text-xs text-blue-600">
                            Uploaded: {submission.oil_analysis_uploaded_at ? formatDate(submission.oil_analysis_uploaded_at) : 'Unknown'}
                          </p>
                        </div>
                      </div>
                      <a
                        href={`/api/files/oil-analysis/${submission.oil_analysis_pdf}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                      >
                        <Download className="h-4 w-4" />
                        Download PDF
                      </a>
                    </div>
                  </div>
                </SubSection>
              )}
            </div>
          </DetailSection>

          {/* Customer Demands */}
          <DetailSection title="Customer Demands">
            <DetailGrid>
              <DetailField label="Protection" value={submission.protection} />
              <DetailField label="Surface Treatment" value={submission.surface_treatment} />
              <DetailField label="Approvals/Classification" value={submission.approvals} />
              <DetailField label="Level of Contamination" value={submission.contamination_level} />
            </DetailGrid>
          </DetailSection>

          {/* General Comments */}
          {submission.general_comments && (
            <DetailSection title="General Comments">
              <p className="text-sm text-gray-900 whitespace-pre-wrap">
                {submission.general_comments}
              </p>
            </DetailSection>
          )}

          {/* Submission Images */}
          {submission.submission_images && submission.submission_images.length > 0 && (
            <DetailSection title="Submission Images">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    {submission.submission_images.length} image{submission.submission_images.length !== 1 ? 's' : ''} uploaded
                  </p>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {submission.submission_images.map((image, index) => (
                    <div
                      key={index}
                      className="group relative aspect-square bg-gray-100 rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-all duration-200"
                      onClick={() => openImageModal(image, index)}
                    >
                      <img
                        src={`/api/files/submission-images/${image.filename}`}
                        alt={image.originalName || `Submission image ${index + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      
                      {/* Fallback for failed image loading */}
                      <div className="hidden w-full h-full items-center justify-center bg-gray-200">
                        <Image className="w-8 h-8 text-gray-400" />
                      </div>
                      
                      {/* Overlay on hover */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                        <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      </div>
                      
                      {/* Image info */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <p className="text-white text-xs truncate" title={image.originalName}>
                          {image.originalName}
                        </p>
                        {image.size && (
                          <p className="text-gray-300 text-xs">
                            {formatFileSize(image.size)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </DetailSection>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {imageModal.isOpen && imageModal.currentImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="relative max-w-5xl max-h-full">
            {/* Close button */}
            <button
              onClick={closeImageModal}
              className="absolute top-4 right-4 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-all duration-200 z-10"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Navigation buttons */}
            {submission.submission_images.length > 1 && (
              <>
                <button
                  onClick={() => navigateImage('prev')}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-all duration-200"
                >
                  <ArrowLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={() => navigateImage('next')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-all duration-200"
                >
                  <ArrowLeft className="w-6 h-6 rotate-180" />
                </button>
              </>
            )}
            
            {/* Image */}
            <div className="bg-white rounded-lg overflow-hidden shadow-2xl">
              <img
                src={`/api/files/submission-images/${imageModal.currentImage.filename}`}
                alt={imageModal.currentImage.originalName || 'Submission image'}
                className="max-w-full max-h-[80vh] object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              
              {/* Fallback for failed image loading */}
              <div className="hidden w-full h-64 items-center justify-center bg-gray-200">
                <div className="text-center">
                  <Image className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">Failed to load image</p>
                </div>
              </div>
              
              {/* Image details */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">
                      {imageModal.currentImage.originalName || `Image ${imageModal.currentIndex + 1}`}
                    </h4>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                      {imageModal.currentImage.size && (
                        <span>{formatFileSize(imageModal.currentImage.size)}</span>
                      )}
                      {imageModal.currentImage.uploadedAt && (
                        <span>Uploaded: {formatDate(imageModal.currentImage.uploadedAt)}</span>
                      )}
                    </div>
                  </div>
                  {submission.submission_images.length > 1 && (
                    <div className="text-sm text-gray-500">
                      {imageModal.currentIndex + 1} of {submission.submission_images.length}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {archiveState.showArchiveConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100">
                <Archive className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">
                Archive Submission
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to archive this submission? It will be moved to the archive where it can be restored later.
                </p>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 text-left">
                    Archive reason (optional)
                  </label>
                  <textarea
                    value={archiveState.archiveReason}
                    onChange={(e) => setArchiveState(prev => ({ ...prev, archiveReason: e.target.value }))}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    rows="3"
                    placeholder="Enter reason for archiving (optional)..."
                  />
                </div>
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={handleArchiveCancel}
                    disabled={archiveState.isArchiving}
                    className="px-4 py-2 bg-gray-100 text-gray-800 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleArchive}
                    disabled={archiveState.isArchiving}
                    className="px-4 py-2 bg-orange-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {archiveState.isArchiving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Archiving...
                      </>
                    ) : (
                      'Archive'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {assignmentState.showAssignModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                <UserPlus className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">
                {submission.assigned_to ? 'Reassign Submission' : 'Assign Submission'}
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 mb-4">
                  Choose a user to assign this submission to, or select "Unassigned" to remove the current assignment.
                </p>
                <div className="text-left">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assign to:
                  </label>
                  <select
                    value={assignmentState.selectedUserId || ''}
                    onChange={(e) => setAssignmentState(prev => ({ 
                      ...prev, 
                      selectedUserId: e.target.value || null 
                    }))}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Unassigned</option>
                    {assignmentState.users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.displayName}
                      </option>
                    ))}
                  </select>
                  {assignmentState.loadingUsers && (
                    <p className="text-xs text-gray-500 mt-1">Loading users...</p>
                  )}
                  {submission.assigned_to && (
                    <p className="text-xs text-gray-600 mt-2">
                      Currently assigned to: {formatAssignedTo(submission.assigned_to)}
                    </p>
                  )}
                </div>
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={handleAssignCancel}
                    disabled={assignmentState.isAssigning}
                    className="px-4 py-2 bg-gray-100 text-gray-800 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAssign}
                    disabled={assignmentState.isAssigning}
                    className="px-4 py-2 bg-blue-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {assignmentState.isAssigning ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Assigning...
                      </>
                    ) : (
                      assignmentState.selectedUserId ? 'Assign' : 'Unassign'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmissionDetail;
